import React from "react";
import { useGoogleLogin, useGoogleOAuth } from "@react-oauth/google";
import { Button } from "./ui/button";
import { toast } from "sonner";

// REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
//
// GoogleLinkButton — used by Settings to attach a Google account to an
// already-authenticated phone-OTP user. Reuses the same /auth/google/callback
// route as the login button, but stamps a localStorage intent flag BEFORE
// kicking off the redirect. The callback page reads the flag and posts the
// code to /auth/google/link instead of /auth/google.
//
// Feb 2026 crash-fix: same defensive split as GoogleSignInButton — if the
// build lacks REACT_APP_GOOGLE_CLIENT_ID, we render an inert placeholder
// instead of calling `useGoogleLogin` (which would crash the whole page).

const INTENT_KEY = "syllox_oauth_intent";

function GoogleIcon({ size = 18 }) {
  return (
    <svg width={size} height={size} viewBox="0 0 48 48" aria-hidden="true">
      <path fill="#FFC107" d="M43.611 20.083H42V20H24v8h11.303c-1.649 4.657-6.08 8-11.303 8-6.627 0-12-5.373-12-12s5.373-12 12-12c3.059 0 5.842 1.154 7.961 3.039l5.657-5.657C34.046 6.053 29.268 4 24 4 12.955 4 4 12.955 4 24s8.955 20 20 20 20-8.955 20-20c0-1.341-.138-2.65-.389-3.917z"/>
      <path fill="#FF3D00" d="M6.306 14.691l6.571 4.819C14.655 15.108 18.961 12 24 12c3.059 0 5.842 1.154 7.961 3.039l5.657-5.657C34.046 6.053 29.268 4 24 4 16.318 4 9.656 8.337 6.306 14.691z"/>
      <path fill="#4CAF50" d="M24 44c5.166 0 9.86-1.977 13.409-5.192l-6.19-5.238C29.211 35.091 26.715 36 24 36c-5.202 0-9.619-3.317-11.283-7.946l-6.522 5.025C9.505 39.556 16.227 44 24 44z"/>
      <path fill="#1976D2" d="M43.611 20.083H42V20H24v8h11.303c-.792 2.237-2.231 4.166-4.087 5.571.001-.001.002-.001.003-.002l6.19 5.238C36.971 39.205 44 34 44 24c0-1.341-.138-2.65-.389-3.917z"/>
    </svg>
  );
}

const BASE_CLS = "bg-white text-zinc-900 hover:bg-zinc-50 border border-zinc-300 dark:bg-white dark:text-zinc-900 dark:hover:bg-zinc-100";

export default function GoogleLinkButton({ label = "Link Google account", testId = "google-link-button", className = "" }) {
  const { clientId } = useGoogleOAuth();
  return clientId
    ? <ConfiguredLinkButton label={label} testId={testId} className={className} />
    : <InertLinkButton label={label} testId={testId} className={className} />;
}

function ConfiguredLinkButton({ label, testId, className }) {
  const login = useGoogleLogin({
    flow: "auth-code",
    ux_mode: "redirect",
    redirect_uri: window.location.origin + "/auth/google/callback",
  });
  const onClick = () => {
    try { localStorage.setItem(INTENT_KEY, "link"); } catch { /* ignore */ }
    login();
  };
  return (
    <Button
      type="button"
      onClick={onClick}
      data-testid={testId}
      className={`${BASE_CLS} ${className}`}
    >
      <span className="mr-2 inline-flex items-center justify-center"><GoogleIcon size={16}/></span>
      {label}
    </Button>
  );
}

function InertLinkButton({ label, testId, className }) {
  const onClick = () => {
    toast.error(
      "Google Sign-In isn't configured for this deployment. Please contact support."
    );
  };
  return (
    <Button
      type="button"
      onClick={onClick}
      data-testid={testId}
      aria-disabled="true"
      className={`${BASE_CLS} opacity-70 ${className}`}
    >
      <span className="mr-2 inline-flex items-center justify-center"><GoogleIcon size={16}/></span>
      {label}
    </Button>
  );
}
